import type { CreateGuestInput, UpdateGuestInput } from '@room-mates/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../shared/api/queryKeys.js';
import { useApi } from '../../shared/auth/useAuth.js';

export function useGuests() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.guests.all, queryFn: () => api.guests.list() });
}

function useGuestInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.guests.all });
    // Deleting a guest also clears them from bed wish lists and from other
    // guests' preferences, so the housing cache is no longer trustworthy.
    await queryClient.invalidateQueries({ queryKey: queryKeys.houses.all });
  };
}

export function useCreateGuest() {
  const api = useApi();
  const invalidate = useGuestInvalidation();
  return useMutation({
    mutationFn: (input: CreateGuestInput) => api.guests.create(input),
    onSuccess: invalidate,
  });
}

export function useUpdateGuest() {
  const api = useApi();
  const invalidate = useGuestInvalidation();
  return useMutation({
    mutationFn: ({ guestId, input }: { guestId: string; input: UpdateGuestInput }) =>
      api.guests.update(guestId, input),
    onSuccess: invalidate,
  });
}

export function useDeleteGuest() {
  const api = useApi();
  const invalidate = useGuestInvalidation();
  return useMutation({
    mutationFn: (guestId: string) => api.guests.remove(guestId),
    onSuccess: invalidate,
  });
}
